import { useState } from 'react';

interface PDFUploadModalProps {
  file: File;
  onSubmit: (data: {
    companyName: string;
    fiscalYear?: number;
    ticker?: string;
  }) => void;
  onCancel: () => void;
}

export default function PDFUploadModal({ file, onSubmit, onCancel }: PDFUploadModalProps) {
  const [companyName, setCompanyName] = useState('');
  const [fiscalYear, setFiscalYear] = useState('');
  const [ticker, setTicker] = useState('');

  const handleSubmit = () => {
    if (!companyName.trim()) {
      alert('Please enter a company name');
      return;
    }

    onSubmit({
      companyName: companyName.trim(),
      fiscalYear: fiscalYear ? parseInt(fiscalYear) : undefined,
      ticker: ticker.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white border border-neutral-200 rounded-lg p-6 w-full max-w-md mx-4 shadow-xl">
        <h2 className="text-2xl font-bold mb-4 text-neutral-900">Upload PDF Annual Report</h2>

        <div className="space-y-4">
          {/* File Info */}
          <div className="bg-neutral-50 rounded-lg p-3 border border-neutral-200">
            <p className="text-sm text-neutral-600 font-medium">File:</p>
            <p className="text-sm font-semibold text-neutral-900 mt-1">{file.name}</p>
            <p className="text-xs text-neutral-500 mt-1">
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>

          {/* Company Name */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-neutral-900">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full bg-white rounded-lg px-3 py-2 border border-neutral-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:outline-none text-neutral-900"
              placeholder="e.g., Apple Inc."
              autoFocus
            />
          </div>

          {/* Fiscal Year */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-neutral-900">
              Fiscal Year
            </label>
            <input
              type="number"
              value={fiscalYear}
              onChange={(e) => setFiscalYear(e.target.value)}
              className="w-full bg-white rounded-lg px-3 py-2 border border-neutral-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:outline-none text-neutral-900"
              placeholder="2024"
              min="1900"
              max="2100"
            />
          </div>

          {/* Ticker Symbol */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-neutral-900">
              Ticker Symbol
            </label>
            <input
              type="text"
              value={ticker}
              onChange={(e) => setTicker(e.target.value.toUpperCase())}
              className="w-full bg-white rounded-lg px-3 py-2 border border-neutral-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:outline-none text-neutral-900 uppercase"
              placeholder="AAPL"
              maxLength={10}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 rounded-lg bg-white hover:bg-neutral-50 border border-neutral-300 font-semibold text-neutral-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!companyName.trim()}
              className="flex-1 px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:bg-neutral-300 disabled:cursor-not-allowed font-semibold text-white transition-colors"
            >
              Upload & Extract
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
